import React, { useEffect } from 'react';
import { Button } from 'antd';
import { ReloadOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { AppButton } from './AppButton';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { FONT_SIZE, FONT_WEIGHT, ICON_SIZE, RADIUS, SPACING } from '../../constants/constants';

export interface AppLoadErrorProps {
  message?: string;
  onRetry?: () => void;
  /**
   * 표 위에 한 줄로 붙이는 변형. 이미 보여줄 데이터(이전 조회 결과)가 있는 화면에서
   * 재조회만 실패했을 때 쓴다 — 화면 전체를 오류로 덮지 않고 표를 유지한 채 알린다.
   */
  inline?: boolean;
  /**
   * 원본 오류 객체(axios 등). 있으면 `message` 문자열의 단어보다 `code`·`response.status`
   * 를 먼저 보고 분류한다 — 문구가 바뀌거나 영문 원문에 우연히 섞인 단어로 잘못
   * 분류되지 않게(2026-10-21 PR #39 사후 리뷰).
   */
  error?: unknown;
}

export type LoadErrorKind = 'network' | 'timeout' | 'server' | 'forbidden' | 'unauthorized' | 'unknown';

const LOAD_ERROR_SUMMARY: Record<LoadErrorKind, string> = {
  network:   '서버에 연결하지 못했습니다',
  timeout:   '응답이 없습니다(시간 초과)',
  server:    '서버 오류가 났습니다',
  forbidden: '권한이 없습니다',
  // 401 은 "권한 없음"이 아니라 세션(토큰) 만료·무효다 — 다시 로그인해야 풀린다.
  // 403 과 같은 문구로 묶으면 사용자가 매장 권한 문제로 오해한다(2026-10-21 리뷰).
  unauthorized: '세션이 만료되었습니다(다시 로그인해 주세요)',
  unknown:   '불러오지 못했습니다',
};

/** 원본 오류에서 분류에 쓸 구조화된 값만 뽑는다(axios: `code`, `response.status`). */
function readErrorDetail(error: unknown): { code?: string; status?: number } {
  if (!error || typeof error !== 'object') return {};
  const e = error as { code?: unknown; response?: { status?: unknown } };
  const code = typeof e.code === 'string' ? e.code : undefined;
  const statusRaw = e.response?.status;
  const status = typeof statusRaw === 'number' ? statusRaw : undefined;
  return { code, status };
}

const NETWORK_CODES = ['ERR_NETWORK', 'ECONNREFUSED', 'ECONNRESET', 'ENOTFOUND', 'EAI_AGAIN'];
const TIMEOUT_CODES = ['ECONNABORTED', 'ETIMEDOUT'];

/**
 * 오류를 화면에 보여줄 한국어 문장으로 분류한다(2026-10-20 코덱 3차 지적).
 *
 * 예전엔 `error.message`를 그대로 본문에 넣어 "Network Error"·"timeout of 10000ms exceeded"·
 * "Request failed with status code 403" 같은 개발자용 영문이 화면에 그대로 노출됐다.
 * 사용자가 다음 행동을 정할 수 있도록 종류만 한 문장으로 알리고, 원문은 작은 글자로 남긴다.
 *
 * 분류 순서(2026-10-21 PR #39 사후 리뷰): ① axios 오류의 `code` ② `response.status`
 * ③ 마지막으로 메시지 단어. 단어만 보면 "권한"·"시간 초과"가 우연히 들어간 문장이나
 * 백엔드가 내려준 문구에 끌려 잘못 분류된다.
 */
export function classifyLoadError(
  message?: string,
  error?: unknown,
): { kind: LoadErrorKind; summary: string } {
  const raw = (message ?? '').toLowerCase();
  const pick = (kind: LoadErrorKind) => ({ kind, summary: LOAD_ERROR_SUMMARY[kind] });
  const { code, status } = readErrorDetail(error);

  if (code) {
    const upper = code.toUpperCase();
    if (TIMEOUT_CODES.includes(upper)) return pick('timeout');
    if (NETWORK_CODES.includes(upper)) return pick('network');
  }

  if (status != null) {
    if (status === 408 || status === 504) return pick('timeout');
    // 401(세션 만료)과 403(권한 없음)은 다음 행동이 다르다 — 401 은 다시 로그인하면 풀리고,
    // 403 은 매장/역할 권한이라 재로그인으로 안 풀린다. 예전엔 둘 다 'forbidden' 이었다
    // (2026-10-21 리뷰).
    if (status === 401) return pick('unauthorized');
    if (status === 403) return pick('forbidden');
    if (status >= 500) return pick('server');
    // 그 밖의 응답 상태(404 · 409 · 429 …)는 서버가 답한 것이라 네트워크 · 시간 초과 · 권한이 아니다 — 본문 문구에 "권한" ·
    // "시간 초과" 낱말이 들어 있어도 끌려가지 않게 단어 판정으로 넘기지 않는다(감사 card-controltower#49).
    return pick('unknown');
  }

  if (/timeout|timed out|etimedout|시간 초과/.test(raw)) return pick('timeout');
  // 구조화된 status 가 없어도 원문에 401 이 남아 있으면 세션 만료로 본다 — "권한"보다 먼저
  // 판정해야 401 응답이 403 처럼 보이지 않는다(2026-10-21 리뷰).
  if (/401|unauthorized/.test(raw)) return pick('unauthorized');
  if (/403|forbidden|권한/.test(raw)) return pick('forbidden');
  if (/network error|failed to fetch|err_network|econnrefused|econnreset|enotfound|서버에 연결/.test(raw)) {
    return pick('network');
  }
  if (/status code 5\d\d|internal server error|bad gateway|service unavailable/.test(raw)) return pick('server');
  return pick('unknown');
}

export const AppLoadError: React.FC<AppLoadErrorProps> = ({ message, onRetry, inline, error }) => {
  const t = useThemeTokens();

  // inline 변형은 원문을 화면에 싣지 않으므로(아래) 원인 추적용으로 콘솔에만 남긴다.
  useEffect(() => {
    // eslint-disable-next-line no-console
    if (inline && message) console.warn('[AppLoadError] 재조회 실패:', message);
  }, [inline, message]);

  if (inline) {
    // 예전엔 `(${message})` 로 axios 영문 원문("Network Error")을 그대로 보였다 — 분류한
    // 한국어 문장만 보이고, 원문은 마우스를 올렸을 때(title)와 콘솔에만 둔다(2026-10-20 리뷰 후속).
    const { kind, summary } = classifyLoadError(message, error);
    return (
      <div
        role="alert"
        title={message || undefined}
        style={{
          display: 'flex', alignItems: 'center', gap: SPACING.xs,
          marginBottom: SPACING.sm, padding: `${SPACING.xs}px ${SPACING.sm}px`,
          background: t.tintWarning, border: `1px solid ${t.lineWarning}`, borderRadius: RADIUS.md,
        }}
      >
        <CloseCircleOutlined style={{ fontSize: ICON_SIZE.sm, color: t.warning, flex: '0 0 auto' }} />
        <div style={{ fontSize: FONT_SIZE.sm, color: t.textPrimary, minWidth: 0 }}>
          최신 목록을 불러오지 못했습니다{message && kind !== 'unknown' ? ` — ${summary}` : ''}
          <span style={{ marginLeft: SPACING.xs, fontSize: FONT_SIZE.xs, color: t.textMuted }}>
            표시된 내용은 이전 조회 결과입니다
          </span>
        </div>
        <div style={{ flex: 1 }} />
        {onRetry && (
          <Button type="link" size="small" icon={<ReloadOutlined />} onClick={onRetry} style={{ flex: '0 0 auto' }}>
            다시 시도
          </Button>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: `${SPACING['3xl']}px 0`, textAlign: 'center' }}>
      <CloseCircleOutlined style={{ fontSize: ICON_SIZE.xxl, color: t.danger, marginBottom: SPACING.sm }} />
      <div style={{ fontSize: FONT_SIZE.lg, fontWeight: FONT_WEIGHT.semibold, color: t.textPrimary, marginBottom: SPACING.xs }}>
        {classifyLoadError(message, error).summary}
      </div>
      {message && (
        <div style={{ fontSize: FONT_SIZE.xs, color: t.textMuted, marginBottom: SPACING.lg, wordBreak: 'break-word' }}>
          {message}
        </div>
      )}
      {!message && <div style={{ marginBottom: SPACING.lg }} />}
      {onRetry && (
        <AppButton variant="primary" icon={<ReloadOutlined />} onClick={onRetry}>
          다시 시도
        </AppButton>
      )}
    </div>
  );
};
